import { produce } from 'immer';
import type { StoreApi } from 'zustand';

const HIDDEN_SITES_STORAGE_KEY = 'geoforge-hidden-site-ids';
const HIDDEN_SITE_PIPELINES_STORAGE_KEY = 'geoforge-hidden-site-pipeline-ids';

type HiddenSiteIds = Record<string, string[]>;

const loadHiddenSiteIds = (): HiddenSiteIds => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(HIDDEN_SITES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: HiddenSiteIds = {};
    for (const [layerId, ids] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(ids) && ids.every(id => typeof id === 'string')) {
        out[layerId] = ids;
      }
    }
    return out;
  } catch (error) {
    console.warn('Failed to load hidden site ids from localStorage:', error);
    return {};
  }
};

const saveHiddenSiteIds = (hiddenSiteIds: HiddenSiteIds): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(HIDDEN_SITES_STORAGE_KEY, JSON.stringify(hiddenSiteIds));
  } catch (error) {
    console.warn('Failed to save hidden site ids to localStorage:', error);
  }
};

const loadHiddenSitePipelineIds = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(HIDDEN_SITE_PIPELINES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch (error) {
    console.warn('Failed to load hidden site pipeline ids from localStorage:', error);
    return [];
  }
};

const saveHiddenSitePipelineIds = (ids: readonly string[]): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(HIDDEN_SITE_PIPELINES_STORAGE_KEY, JSON.stringify(ids));
  } catch (error) {
    console.warn('Failed to save hidden site pipeline ids to localStorage:', error);
  }
};

export type SiteReportTarget = {
  layerId: string;
  rowId: string;
  /** Criterion tab (id or key) to open the report on; defaults to overview. */
  initialTab?: string;
} | null;

/**
 * The site currently selected on the map. Selecting a site highlights it and
 * marks its row(s) in the Sites panel active — it does NOT open the report
 * drawer (that's the panel's "Report" button). A feature added to several Site
 * Types produces one `layer_row` per type that all share a `featureId`, so we
 * key selection by `featureId` to mark every matching row active across Site
 * Types. Manually-added sites have no `featureId`; for those we fall back to
 * the single `rowId`.
 */
export type ActiveSite = { featureId: string | null; rowId: string } | null;

export type SitesPanelState = {
  /** Per-site-layer set of row ids that are hidden on the map. */
  hiddenSiteIds: HiddenSiteIds;
  /**
   * Site layers hidden wholesale. Mirrors layer-group visibility in the Layers
   * tab: a site renders only when neither its pipeline nor its own row is
   * hidden, so this survives independently of per-row state.
   */
  hiddenSitePipelineIds: string[];
  /** Currently-open site report target, if any. */
  siteReportTarget: SiteReportTarget;
  /** The site selected (highlighted) on the map, if any. */
  activeSite: ActiveSite;
  /** Whether a given row is the active selection (matches by featureId, else rowId). */
  isSiteActive: (rowId: string, featureId: string | null) => boolean;
  setActiveSite: (featureId: string | null, rowId: string) => void;
  clearActiveSite: () => void;
  isSiteHidden: (layerId: string, rowId: string) => boolean;
  setSiteHidden: (layerId: string, rowId: string, hidden: boolean) => void;
  isSitePipelineHidden: (layerId: string) => boolean;
  /** Hide or show a whole Site Pipeline (its section header switch). */
  setSitePipelineHidden: (layerId: string, hidden: boolean) => void;
  /** Hide or show every Site Pipeline at once (the panel header switch). */
  setAllSitePipelinesHidden: (layerIds: readonly string[], hidden: boolean) => void;
  /** Drop all hidden entries for a layer (e.g. when the layer is removed). */
  clearHiddenSitesForLayer: (layerId: string) => void;
  /** Drop hidden row and pipeline entries whose layer id is not in `validLayerIds`. */
  gcHiddenSiteIds: (validLayerIds: readonly string[]) => void;
  openSiteReport: (layerId: string, rowId: string, options?: { initialTab?: string }) => void;
  closeSiteReport: () => void;
  clearSitesPanel: () => void;
};

const initialSitesPanelState = {
  hiddenSiteIds: loadHiddenSiteIds(),
  hiddenSitePipelineIds: loadHiddenSitePipelineIds(),
  siteReportTarget: null as SiteReportTarget,
  activeSite: null as ActiveSite,
};

export const createSitesPanelSlice = (
  set: StoreApi<any>['setState'],
  get: () => SitesPanelState
): SitesPanelState => ({
  ...initialSitesPanelState,
  isSiteHidden: (layerId, rowId) => {
    const ids = get().hiddenSiteIds[layerId];
    return Array.isArray(ids) && ids.includes(rowId);
  },
  setSiteHidden: (layerId, rowId, hidden) =>
    set(
      produce((draft: SitesPanelState) => {
        const existing = draft.hiddenSiteIds[layerId] ?? [];
        const isHidden = existing.includes(rowId);
        if (hidden && !isHidden) {
          draft.hiddenSiteIds[layerId] = [...existing, rowId];
        } else if (!hidden && isHidden) {
          const next = existing.filter(id => id !== rowId);
          if (next.length === 0) {
            delete draft.hiddenSiteIds[layerId];
          } else {
            draft.hiddenSiteIds[layerId] = next;
          }
        } else {
          return;
        }
        saveHiddenSiteIds(draft.hiddenSiteIds);
      })
    ),
  isSitePipelineHidden: layerId => get().hiddenSitePipelineIds.includes(layerId),
  setSitePipelineHidden: (layerId, hidden) =>
    set(
      produce((draft: SitesPanelState) => {
        const isHidden = draft.hiddenSitePipelineIds.includes(layerId);
        if (hidden === isHidden) return;
        draft.hiddenSitePipelineIds = hidden
          ? [...draft.hiddenSitePipelineIds, layerId]
          : draft.hiddenSitePipelineIds.filter(id => id !== layerId);
        saveHiddenSitePipelineIds(draft.hiddenSitePipelineIds);
      })
    ),
  setAllSitePipelinesHidden: (layerIds, hidden) =>
    set(
      produce((draft: SitesPanelState) => {
        // Both directions touch only the given layers. Ids outside `layerIds`
        // are left alone here, but they don't survive long: `gcHiddenSiteIds`
        // runs against one workspace's layers and trims the rest.
        const next = hidden
          ? Array.from(new Set([...draft.hiddenSitePipelineIds, ...layerIds]))
          : draft.hiddenSitePipelineIds.filter(id => !layerIds.includes(id));
        if (next.length === draft.hiddenSitePipelineIds.length) return;
        draft.hiddenSitePipelineIds = next;
        saveHiddenSitePipelineIds(draft.hiddenSitePipelineIds);
      })
    ),
  clearHiddenSitesForLayer: layerId =>
    set(
      produce((draft: SitesPanelState) => {
        if (draft.hiddenSiteIds[layerId]) {
          delete draft.hiddenSiteIds[layerId];
          saveHiddenSiteIds(draft.hiddenSiteIds);
        }
        if (draft.hiddenSitePipelineIds.includes(layerId)) {
          draft.hiddenSitePipelineIds = draft.hiddenSitePipelineIds.filter(id => id !== layerId);
          saveHiddenSitePipelineIds(draft.hiddenSitePipelineIds);
        }
      })
    ),
  gcHiddenSiteIds: validLayerIds =>
    set(
      produce((draft: SitesPanelState) => {
        const valid = new Set(validLayerIds);
        let mutated = false;
        for (const layerId of Object.keys(draft.hiddenSiteIds)) {
          if (!valid.has(layerId)) {
            delete draft.hiddenSiteIds[layerId];
            mutated = true;
          }
        }
        if (mutated) saveHiddenSiteIds(draft.hiddenSiteIds);

        const trimmed = draft.hiddenSitePipelineIds.filter(layerId => valid.has(layerId));
        if (trimmed.length !== draft.hiddenSitePipelineIds.length) {
          draft.hiddenSitePipelineIds = trimmed;
          saveHiddenSitePipelineIds(draft.hiddenSitePipelineIds);
        }
      })
    ),
  openSiteReport: (layerId, rowId, options) =>
    set(
      produce((draft: SitesPanelState) => {
        draft.siteReportTarget = { layerId, rowId, initialTab: options?.initialTab };
      })
    ),
  closeSiteReport: () =>
    set(
      produce((draft: SitesPanelState) => {
        draft.siteReportTarget = null;
      })
    ),
  isSiteActive: (rowId, featureId) => {
    const active = get().activeSite;
    if (!active) return false;
    // When the selection carries a featureId, every row sharing it (across Site
    // Types) is active. Otherwise fall back to exact row identity.
    if (active.featureId != null) return featureId === active.featureId;
    return active.rowId === rowId;
  },
  setActiveSite: (featureId, rowId) =>
    set(
      produce((draft: SitesPanelState) => {
        draft.activeSite = { featureId, rowId };
      })
    ),
  clearActiveSite: () =>
    set(
      produce((draft: SitesPanelState) => {
        draft.activeSite = null;
      })
    ),
  clearSitesPanel: () =>
    set(
      produce((draft: SitesPanelState) => {
        draft.siteReportTarget = null;
        draft.activeSite = null;
      })
    ),
});
